import {
  IQuestionModel,
  QuestionAnswerMap
} from '@care-portals/api-interfaces';

const QuestionModel = [
  {
    field: 'microGlp1Followup_sex',
    label: 'What was your sex assigned at birth?',
    type: 'select',
    extras: { sendToBeluga: false },
    answer: {
      type: 'single_select',
      options: [
        { value: 'Male', key: 'male' },
        { value: 'Female', key: 'female' }
      ]
    }
  },
  {
    // TODO if BMI is under 19 for a treatment naive patient → disqualified; if BMI is under 23 for a patient currently on treatment → disqualified
    label: 'What is your:',
    field: 'microGlp1Followup_weightAndHeight2025',
    type: 'info',
    required: false,
    answer: {
      type: 'bmi',
      showLabel: true,
      bmiLabel: 'Your BMI is',
      heightLabel: 'What is your height in feet and inches?',
      weightLabel: 'What is your weight in pounds?'
    }
  },
  {
    field: 'microGlp1Followup_healthChanges',
    label:
      'Have there been any health changes, medication changes, ED visits or hospitalizations since your last check-in?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Yes', key: 'yes' },
        { value: 'No', key: 'no' }
      ]
    }
  },
  {
    field: 'microGlp1Followup_healthChangesDetails',
    label: 'Please tell us more about the changes in your health',
    type: 'info',
    answer: {
      type: 'text',
      rows: 4,
      name: 'microGlp1Followup_healthChangesDetails'
    }
  },
  {
    field: 'microGlp1Followup_medicationResponse',
    label: 'How are you doing on the current medication?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Well', key: 'well' },
        { value: 'Not well', key: 'not_well' }
      ]
    }
  },
  {
    field: 'microGlp1Followup_medicationIssues',
    label: 'Please tell us more about what you are experiencing',
    type: 'info',
    answer: {
      type: 'text',
      rows: 4,
      name: 'microGlp1Followup_medicationIssues'
    }
  },
  {
    field: 'microGlp1Followup_sideEffects',
    label: 'Have you had any side effects from the current medication?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Yes', key: 'yes' },
        { value: 'No', key: 'no' }
      ]
    }
  },
  {
    field: 'microGlp1Followup_sideEffectsDetails',
    label: 'Please tell us more about the side effects you are experiencing',
    type: 'info',
    answer: {
      type: 'text',
      rows: 4,
      name: 'microGlp1Followup_sideEffectsDetails'
    }
  },
  {
    field: 'microGlp1Followup_additionalQuestions',
    label: 'What other information or questions do you have for the doctor?',
    type: 'info',
    answer: {
      type: 'text',
      rows: 4,
      name: 'microGlp1Followup_additionalQuestions'
    }
  },
  {
    field: 'microGlp1Followup_consentTruthfulness',
    label: 'Consent (Truthfulness):',
    type: 'select',
    educationalSlide:
      'Please attest to the following confirming that all information you have provided to us is true and complete.\n\nI verify that I am the patient and that I have answered the questions asked in this intake form. I confirm that I have reviewed and understood all the questions asked of me. I attest that the answers and information I have provided in this questionnaire is true and complete to the best of my knowledge. I understand that it is critical to my health to share complete health information with my doctor. I will not hold the doctor or affiliated medical practice responsible for any oversights or omissions, whether intentional or not, in the information that I provided.',
    answer: {
      type: 'single_select',
      options: [
        {
          value:
            'I have read the above information and I do NOT wish to continue', // // Disqualifying
          class: 'option-decline',
          key: 'non_consent'
        },
        {
          value:
            'I have read the above information and I do consent and wish to move forward',
          class: 'option-consent',
          key: 'consent'
        }
      ]
    }
  },
  {
    field: 'microGlp1Followup_consentTreatment',
    label: 'Consent (Weight Maintenance Treatment):',
    type: 'select',
    educationalSlide:
      'Indication for Use:\nYou are requesting micro-dose therapy with a GLP-based products receptor agonist (e.g., semaglutide) or a GIP/GLP-based products receptor agonist (e.g., tirzepatide) to support metabolic health, appetite regulation, reduction of inflammation, and cellular well-being. These very low doses are below the standard therapeutic range and are intended to explore potential wellness benefits. This is considered "off-label" and is not an FDA approved use of this medication.\n\nCompounded Formulations:\nCompounded semaglutide and tirzepatide are custom-prepared by a licensed pharmacist to meet your individual needs. This allows for tailored dosing schedules, alternative routes of administration, or the addition of agents to help minimize side effects. Because compounded medications are not subject to the same extensive clinical trials as FDA-approved products, their consistency, efficacy, and safety profiles may vary.\n\nPotential Benefits:\n• Support for metabolic balance and appetite control\n• Potential reduction in systemic inflammation\n• Early/preliminary neuroprotective or anti-aging effects\n• Enhancement of cellular "well-being"\n\nPotential Side Effects:\nAlthough micro-dosing may lower risk, these therapies can still cause adverse reactions, including:\n• Common: nausea, vomiting, diarrhea or constipation, decreased appetite, indigestion\n• Serious: pancreatitis (severe abdominal pain, nausea, vomiting which can be deadly), gallbladder issues, kidney impairment, allergic reactions (rash, itching, swelling), gastroparesis (slowed gastric emptying)\n\nRisks and Considerations:\nCompounded formulations may vary in potency and purity. Please obtain your medication from the pharmacy we designate to ensure quality. Inform your provider immediately if you experience side effects.\n\nMonitoring and Follow-Up:\nRegular follow-up visits will assess your response, adjust dosing, and monitor for side effects. We may request periodic verification of your progress.\n\nAcknowledgment and Consent:\nI have read and understand the information above regarding micro-dose GLP-based products/GIP-GLP-based products therapy, including its compounded nature, potential benefits, risks, and monitoring requirements.',
    answer: {
      type: 'single_select',
      options: [
        {
          value:
            'I have read the above information and I do NOT wish to continue', // Disqualifying
          class: 'option-decline',
          key: 'non_consent'
        },
        {
          value:
            'I have read and understand the above information and I do consent and wish to move forward with this treatment plan',
          class: 'option-consent',
          key: 'consent'
        }
      ]
    }
  },
  {
    field: 'microGlp1Followup_complete',
    isLast: true,
    label: `Thank you for completing this questionnaire, please select "Finish" to submit`,
    extras: { sendToBeluga: false },
    type: 'info',
    answer: {}
  },
  {
    class: 'ineligible',
    field: 'microGlp1Followup_didNotConsent',
    reject: true,
    label: 'We need your consent to continue',
    subLabel:
      'You did not agree to the terms shown on the previous step. Without that consent, we cannot move forward with your visit or prescribe treatment.',
    extras: { sendToBeluga: false },
    blockQuestionFlow: true,
    answer: {},
    type: 'info',
    isLast: true
  },
  {
    class: 'ineligible',
    field: 'microGlp1Followup_ineligible',
    label: "We're sorry you don't qualify",
    subLabel: "We're not able to prescribe for you today",
    extras: { sendToBeluga: false },
    answer: {},
    reject: true,
    type: 'info',
    isLast: true
  }
] as const;

const _type = QuestionModel;
export type MicroGlp1FollowupFieldAnswers = Partial<
  QuestionAnswerMap<typeof _type>
>;

export default QuestionModel;

